// ProductCard.js
import {
  defineComponent,
  computed,
  toRefs,
  ref,
  onMounted,
  onUnmounted,
  watch,
  nextTick,
  inject,
} from "/Scripts/vue.esm-browser.js";
import { transformPresentImageUrl } from "/Scripts/tools.js";

const html = String.raw;
import FlightInfo from "/Component/Shop/product/FlightInfo.js";

const WEEKDAYS = ["日", "一", "二", "三", "四", "五", "六"];

const parseSlashDate = (dateString) => {
  if (!dateString) return null;
  const [year, month, day] = dateString.split("/").map(Number);
  return new Date(year, month - 1, day);
};

const formatEnsureDateHtml = (groups = []) => {
  const grouped = new Map();

  groups
    .filter((group) => group.hotType == 6 && group.leaveDate)
    .slice()
    .sort((a, b) => parseSlashDate(a.leaveDate) - parseSlashDate(b.leaveDate))
    .forEach((group) => {
      const [year, month, day] = group.leaveDate.split("/");
      const key = `${year}-${parseInt(month, 10)}`;
      const dayNum = parseInt(day, 10);

      if (!grouped.has(key)) grouped.set(key, new Set());
      grouped.get(key).add(dayNum);
    });

  return Array.from(grouped.entries())
    .map(([key, daySet]) => {
      const [, month] = key.split("-");
      const days = Array.from(daySet)
        .sort((a, b) => a - b)
        .join(".");
      return `<mark>${month}月</mark>${days}`;
    })
    .join("、");
};

export const buildCardViewModel = (item) => {
  const groups = item?.groups ?? [];
  // const picture = item?.picture?.replace(
  //   "ncapi.bwt.com.tw",
  //   "ncapi.ibwt.com.tw",
  // );

  const picture = item?.picture || "";
  const processedPicture = picture
    ? transformPresentImageUrl(picture, { targetHeight: 192 })
    : "";

  const groupDates = groups
    .map((group) => {
      const dateObj = parseSlashDate(group.leaveDate);
      return {
        leaveDate: group.leaveDate
          ? `${group.leaveDate.split("/")[1]}/${group.leaveDate.split("/")[2]}(${WEEKDAYS[dateObj.getDay()]})`
          : null,
        type: `type${group.status}`,
        groupCode: group.groupCode,
        sortDate: dateObj,
      };
    })
    .sort((a, b) => a.sortDate - b.sortDate)
    .map(({ sortDate, ...rest }) => rest);

  const groupMinPrice = groups.reduce(
    (minGroup, currentGroup) =>
      currentGroup.price < minGroup.price
        ? {
            price: currentGroup.price,
            sitePrice: currentGroup.sitePrice,
          }
        : minGroup,
    {
      price: groups[0]?.price ?? 99999999,
      sitePrice: groups[0]?.sitePrice ?? 99999999,
    },
  );

  const earliestGroup = groups.reduce((earliest, current) => {
    if (!earliest?.leaveDate) return current;
    const earliestDate = parseSlashDate(earliest.leaveDate);
    const currentDate = parseSlashDate(current.leaveDate);
    return currentDate < earliestDate ? current : earliest;
  }, groups[0] || {});

  const discountMap = new Map();
  for (const group of groups) {
    if (!group?.discount || !group?.leaveDate) continue;
    const items = discountMap.get(group.discount) ?? [];
    items.push({ leaveDate: group.leaveDate, groupCode: group.groupCode });
    discountMap.set(group.discount, items);
  }

  const discountInfo = Array.from(discountMap.entries()).map(
    ([discount, items]) => {
      const seen = new Set();
      const uniqAll = [];

      for (const current of items) {
        if (seen.has(current.leaveDate)) continue;
        seen.add(current.leaveDate);
        uniqAll.push({
          date: parseSlashDate(current.leaveDate),
          groupCode: current.groupCode,
          rawDate: current.leaveDate,
        });
      }

      uniqAll.sort((a, b) => a.date - b.date);

      const top3 = uniqAll.slice(0, 3);
      const monthGroups = [];
      let currentMonth = null;
      let currentList = [];
      for (const { date } of top3) {
        const month = date.getMonth() + 1;
        const day = date.getDate();
        if (month !== currentMonth) {
          if (currentList.length > 0) monthGroups.push(currentList);
          currentList = [{ month, day }];
          currentMonth = month;
        } else {
          currentList.push({ month, day });
        }
      }
      if (currentList.length > 0) monthGroups.push(currentList);

      let leaveDateStr = monthGroups
        .map(
          (group) =>
            `${group[0].month}月${group.map((value) => value.day).join(".")}`,
        )
        .join("、");
      if (uniqAll.length > 3) leaveDateStr += "…";

      return {
        discount,
        leaveDateStr,
        firstGroupCode: uniqAll[0]?.groupCode ?? null,
        key: `${discount}|${uniqAll[0]?.groupCode ?? "none"}`,
      };
    },
  );

  const ensureDateHtml = formatEnsureDateHtml(groups);

  return {
    processedPicture,
    groupDates,
    visibleGroupDates: groupDates
      .filter((group) => group.leaveDate)
      .slice(0, 10),
    groupMinPrice,
    earliestGroup,
    discountInfo,
    ensureDateHtml,
  };
};

export default defineComponent({
  name: "ProductCard",
  components: {
    FlightInfo,
  },
  props: {
    item: {
      type: Object,
      required: true,
    },
    isAllProduct: {
      type: Boolean,
      default: false,
    },
    isSearch: {
      type: Boolean,
      default: false,
    },
    groupShowType: {
      type: String,
      required: true,
    },
  },
  emits: [],
  setup(props, { emit }) {
    const { item } = toRefs(props);
    const cruiseDialogPopup = inject("cruiseDialogPopup", ref(false));
    const cruiseName = inject("cruiseName");
    const compareGroups = inject("compareGroups");
    const flights = ref([]);
    const isImageLoaded = ref(false);

    // 響應式螢幕寬度偵測
    const screenWidth = ref(window.innerWidth);

    // 計算是否應該載入圖片（基於CSS媒體查詢的條件）
    const shouldLoadImage = computed(() => {
      // 當是horizontal模式且螢幕寬度 <= 991px時，.A區域會被隱藏，不需要載入圖片
      if (props.groupShowType === "horizontal" && screenWidth.value <= 991) {
        return false;
      }
      return true;
    });
    const shouldShowImagePlaceholder = computed(
      () =>
        shouldLoadImage.value &&
        Boolean(processedPicture.value) &&
        !isImageLoaded.value,
    );

    // 監聽視窗大小變化
    const handleResize = () => {
      screenWidth.value = window.innerWidth;
    };
    const cardViewModel = computed(
      () => item.value?.cardViewModel || buildCardViewModel(item.value),
    );
    const processedPicture = computed(
      () => cardViewModel.value.processedPicture,
    );
    const groupDates = computed(() => cardViewModel.value.groupDates);
    const visibleGroupDates = computed(
      () => cardViewModel.value.visibleGroupDates,
    );
    const groupMinPrice = computed(() => cardViewModel.value.groupMinPrice);
    const earliestGroup = computed(() => cardViewModel.value.earliestGroup);
    const discountInfo = computed(() => cardViewModel.value.discountInfo);
    const ensureDateHtml = computed(() => cardViewModel.value.ensureDateHtml);
    const addDayOfWeek = (dateString) => {
      if (!dateString) {
        return "";
      }
      // 將日期字符串轉換為 Date 對象
      const date = new Date(dateString);

      // 獲取星期幾（0-6，0 表示星期日）
      const dayOfWeek = date.getDay();

      // 將數字轉換為中文表示
      const chineseDayOfWeek = ["日", "一", "二", "三", "四", "五", "六"][
        dayOfWeek
      ];

      // 返回格式化後的字符串
      return `${dateString}（${chineseDayOfWeek}）`;
    };
    const getFlightData = inject("getFlightData");
    const getCruiseData = inject("getCruiseData");
    const nowCruiseKey = inject("nowCruiseKey");

    const getFlights = async (event) => {
      event.stopPropagation(); // 阻止事件往外層冒泡
      event.preventDefault();
      if (flights.value.length === 0) {
        const flightData = await getFlightData(earliestGroup.value.groupCode);
        //先把航班分組 航班可能有多組
        const groupedData = flightData.reduce((acc, item) => {
          const key = item.bookSq;
          if (!acc[key]) {
            acc[key] = [];
          }
          acc[key].push(item);
          return acc;
        }, {});
        flights.value = Object.values(groupedData);
      }
      toggleFlightDetails(event);
    };
    const getCruises = async (event) => {
      event.stopPropagation(); // 阻止事件往外層冒泡
      event.preventDefault();
      await getCruiseData(
        item.value.mainGroupCode,
        props.isAllProduct ? earliestGroup.value.groupCode : "0",
      );
      nowCruiseKey.value =
        item.value.mainGroupCode +
        "|" +
        (props.isAllProduct ? earliestGroup.value.groupCode : "0");

      cruiseName.value = item.value.groupName;
      cruiseDialogPopup.value = true;
    };
    /**
     * 切換航班詳情顯示狀態並更新UI元素
     * @param {HTMLElement} clickedElement - 被點擊的元素
     */
    const toggleFlightDetails = (event) => {
      event.stopPropagation();
      event.preventDefault();
      // 找到相關元素
      const card = event.target.closest(".card");
      const flightWrap = card.querySelector(".flight_wrap");
      const chevronIcon = card.querySelector(".fa-circle-chevron-down");
      const flightButton = card.querySelector(".flight_check");

      // 檢查是否為小屏幕
      const isSmallScreen = window.innerWidth < 991;

      // 獲取當前顯示狀態
      const isCurrentlyHidden =
        flightWrap.style.display === "none" || !flightWrap.style.display;

      // 根據當前狀態決定新的顯示狀態
      const newDisplay = isCurrentlyHidden ? "flex" : "none";
      flightWrap.style.display = newDisplay;

      // 根據新狀態設置UI元素樣式
      updateUIElements(
        flightButton,
        chevronIcon,
        isCurrentlyHidden,
        isSmallScreen,
      );
    };

    /**
     * 根據顯示狀態和屏幕尺寸更新UI元素樣式
     * @param {HTMLElement} button - 航班詳情按鈕
     * @param {HTMLElement} icon - 箭頭圖標
     * @param {boolean} isExpanding - 是否正在展開詳情
     * @param {boolean} isSmallScreen - 是否為小屏幕
     */
    const updateUIElements = (button, icon, isExpanding, isSmallScreen) => {
      // 顏色配置 - 集中管理所有可能的顏色方案
      const colors = {
        expanded: {
          default: "#969696",
          small: "#94b4c8",
        },
        collapsed: {
          default: "#be9f73",
          small: "#50809f",
        },
      };

      // 判斷當前狀態應用的顏色
      const colorSet = isExpanding ? colors.expanded : colors.collapsed;
      const buttonColor = isSmallScreen ? colorSet.small : colorSet.default;

      // 設置按鈕顏色
      button.style.color = buttonColor;

      // 僅在小屏幕下處理圖標顯示邏輯
      if (isSmallScreen) {
        icon.style.display = isExpanding ? "none" : "";
      }
    };
    const goProduct = (groupCode, event) => {
      // 阻止事件冒泡，避免觸發其他點擊事件
      event.stopPropagation();
      event.preventDefault();
      window.open(`/tour/${groupCode}`, "_blank");
    };
    const getTargetUrl = (groupCode) => {
      let url = "null";
      if (groupCode) {
        url = `/tour/${groupCode}`;
      }
      return url; // 返回目標URL
    };
    const handleClick = (event, groupCode) => {
      if (!groupCode) {
        event.preventDefault();
      }
    };

    const favorite = ($event) => {
      $event.stopPropagation();
      setTimeout(() => {
        UpdateClass();
      }, 2000);
    };

    const showMoreIcon = computed(() => {
      const isSmallScreen = window.innerWidth <= 576;
      const dateCount = visibleGroupDates.value.length;
      if (props.groupShowType === "vertical") {
        return isSmallScreen ? dateCount > 2 : dateCount > 3;
      } else {
        return isSmallScreen ? dateCount > 3 : true;
      }
    });
    const showMoreText = computed(() => {
      const dateCount = visibleGroupDates.value.length;
      if (groupDates.value.length > visibleGroupDates.value.length) return true;
      if (props.groupShowType === "vertical") {
        return screenWidth.value <= 576 ? dateCount > 2 : dateCount > 3;
      }
      return screenWidth.value <= 576 ? dateCount > 3 : dateCount > 5;
    });
    const handleImageLoaded = () => {
      isImageLoaded.value = true;
    };

    onMounted(() => {
      nextTick(() => {
        BindCompareButton(compareGroups.value);
      });
    });

    // 清理事件監聽器
    onUnmounted(() => {
      window.removeEventListener("resize", handleResize);
    });

    watch(
      [shouldLoadImage, processedPicture],
      ([shouldLoad, picture]) => {
        isImageLoaded.value = false;
        if (!shouldLoad || !picture) return;
      },
      { immediate: true },
    );
    return {
      groupDates,
      groupMinPrice,
      group: earliestGroup.value || {},
      earliestGroup,
      addDayOfWeek,
      getFlights,
      getCruises,
      flights,
      toggleFlightDetails,
      showMoreIcon,
      showMoreText,
      goProduct,
      favorite,
      getTargetUrl,
      isImageLoaded,
      handleClick,
      handleImageLoaded,
      shouldLoadImage,
      shouldShowImagePlaceholder,
      discountInfo,
      ensureDateHtml,
      visibleGroupDates,
      processedPicture,
    };
  },
  template: html`
    <a
      :href="getTargetUrl(earliestGroup?.groupCode || item.mainGroupCode)"
      target="_blank"
      class="card"
      @click="handleClick(earliestGroup?.groupCode || item.mainGroupCode)"
    >
      <div class="card_container">
        <div class="A">
          <div
            class="product_img"
            :class="{ 'product_img--deferred': shouldShowImagePlaceholder }"
          >
            <div
              v-if="shouldShowImagePlaceholder"
              class="product_img_placeholder"
              aria-hidden="true"
            ></div>
            <picture>
              <source
                :srcset="processedPicture+'/.avif'"
                media="(min-width: 992px)"
              />
              <source :srcset="processedPicture" media="(min-width: 992px)" />
              <img
                loading="lazy"
                decoding="async"
                src="data:image/gif;base64,R0lGODlhAQABAAD/ACwAAAAAAQABAAACADs="
                :alt="item.groupName"
                @load="handleImageLoaded"
                @error="handleImageLoaded"
              />
            </picture>
            <div v-if="isSearch" class="product_location_title">
              {{item.areaName+'-'+removeParenthesesContent(item.subAreaName)}}
            </div>
          </div>
          <div class="product_info_wrap">
            <div class="product_depart_date">
              {{addDayOfWeek(group.leaveDate)}}
            </div>
            <div
              class="product_status"
              :class="getStatusClass(group.status)"
            ></div>
          </div>
        </div>
        <div class="B">
          <div
            class="card_product_title"
            :class="{ 'text-indent': ((groupShowType === 'uni' ? group.groupName : item.groupName)||'').startsWith('【') }"
          >
            {{ groupShowType === 'uni' ? group.groupName : item.groupName }}
          </div>
        </div>
        <div class="C">
          <div class="card_discount_wrap">
            <div
              class="card_state"
              v-if="ensureDateHtml && groupShowType!='uni'"
            >
              <svg
                class="icon-state"
                xmlns="http://www.w3.org/2000/svg"
                width="19"
                height="19"
                viewBox="0 0 19 19"
                fill="none"
              >
                <mask
                  id="mask0_1368_134"
                  style="mask-type:alpha"
                  maskUnits="userSpaceOnUse"
                  x="0"
                  y="0"
                  width="19"
                  height="19"
                >
                  <rect width="19" height="19" fill="#D9D9D9"></rect>
                </mask>
                <g mask="url(#mask0_1368_134)">
                  <path
                    d="M16.625 6.33281C17.0472 6.33281 17.4167 6.49114 17.7333 6.80781C18.05 7.12448 18.2083 7.49392 18.2083 7.91614V9.49948C18.2083 9.59184 18.1984 9.6908 18.1787 9.79635C18.1589 9.90191 18.1292 10.0009 18.0896 10.0932L15.7146 15.6745C15.5958 15.9384 15.3979 16.1627 15.1208 16.3474C14.8438 16.5321 14.5535 16.6245 14.25 16.6245H7.91668C7.48126 16.6245 7.10852 16.4694 6.79845 16.1594C6.48838 15.8493 6.33334 15.4766 6.33334 15.0411V6.98594C6.33334 6.77482 6.37623 6.57361 6.46199 6.38229C6.54775 6.19097 6.6632 6.02274 6.80834 5.8776L11.1031 1.6026C11.3011 1.41788 11.5353 1.30573 11.8057 1.26614C12.0762 1.22656 12.3368 1.27274 12.5875 1.40469C12.8382 1.53663 13.0196 1.72135 13.1318 1.95885C13.2439 2.19635 13.267 2.44045 13.2011 2.69114L12.3104 6.33281H16.625ZM3.16668 16.6245C2.73126 16.6245 2.35852 16.4694 2.04845 16.1594C1.73838 15.8493 1.58334 15.4766 1.58334 15.0411V7.91614C1.58334 7.48073 1.73838 7.10798 2.04845 6.79791C2.35852 6.48784 2.73126 6.33281 3.16668 6.33281C3.60209 6.33281 3.97484 6.48784 4.28491 6.79791C4.59498 7.10798 4.75001 7.48073 4.75001 7.91614V15.0411C4.75001 15.4766 4.59498 15.8493 4.28491 16.1594C3.97484 16.4694 3.60209 16.6245 3.16668 16.6245Z"
                    fill="#D00003"
                  ></path>
                </g>
              </svg>
              <span class="dateText">
                <span class="dateText_box">
                  <span>
                    <mark>保證出發：</mark>
                    <span v-html="ensureDateHtml"></span>
                  </span>
                </span>
              </span>
            </div>
            <template v-if="groupShowType == 'uni'">
              <div
                class="card_discount"
                v-for="discount in discountInfo"
                :key="discount.key"
              >
                <a :href="'/tour/'+discount.firstGroupCode" target="_blank">
                  <span class="card_discount-main">
                    <svg
                      class="icon-discount_gift"
                      xmlns="http://www.w3.org/2000/svg"
                      width="16"
                      height="16"
                      viewBox="0 0 16 16"
                      fill="none"
                    >
                      <g clip-path="url(#clip0_7471_58273)">
                        <path
                          d="M6.12787 0.58619C6.19527 0.628836 6.2625 0.671753 6.32955 0.714957C6.56604 0.833739 6.66883 0.820045 6.92574 0.770587C7.30428 0.708581 7.67337 0.685613 8.05694 0.68548C8.11031 0.685316 8.16368 0.685151 8.21866 0.684982C8.50216 0.687441 8.76744 0.70965 9.04547 0.763633C9.60636 0.870676 9.92216 0.688021 10.3927 0.415866C11.0233 0.0724135 11.8931 -0.0953636 12.5984 0.0558261C13.1122 0.20757 13.5509 0.476311 13.8444 0.934372C14.1148 1.4401 14.1308 1.95975 14.0144 2.51527C14.0665 2.51438 14.1186 2.51348 14.1722 2.51257C14.366 2.50966 14.5597 2.50753 14.7534 2.50592C14.8372 2.50506 14.9209 2.5039 15.0047 2.50243C15.1252 2.50036 15.2458 2.4994 15.3663 2.49865C15.4751 2.49731 15.4751 2.49731 15.586 2.49594C15.7922 2.51723 15.8568 2.53994 16.0002 2.68548C16.0286 2.83835 16.0286 2.83835 16.0287 3.02502C16.0295 3.12982 16.0295 3.12982 16.0303 3.23673C16.0297 3.31303 16.029 3.38933 16.0284 3.46562C16.0284 3.54384 16.0284 3.62206 16.0286 3.70027C16.0287 3.86416 16.028 4.02802 16.0267 4.19191C16.0251 4.40198 16.0252 4.61201 16.0259 4.82208C16.0263 4.98359 16.0259 5.14508 16.0252 5.30658C16.0249 5.38404 16.0249 5.46149 16.0251 5.53895C16.0253 5.64719 16.0242 5.75543 16.0231 5.86367C16.0228 5.92527 16.0225 5.98687 16.0222 6.05034C16.0002 6.20321 16.0002 6.20321 15.9392 6.29721C15.7712 6.41445 15.6236 6.38961 15.4222 6.38406C15.35 6.38253 15.2778 6.38099 15.2034 6.37941C15.1204 6.37645 15.1204 6.37645 15.0357 6.37342C15.0358 6.446 15.0358 6.446 15.036 6.52004C15.0385 7.69624 15.0404 8.87244 15.0415 10.0486C15.0421 10.6174 15.0429 11.1862 15.0442 11.755C15.0454 12.2507 15.0461 12.7463 15.0463 13.242C15.0465 13.5045 15.0468 13.7671 15.0477 14.0296C15.0484 14.2766 15.0487 14.5235 15.0485 14.7705C15.0485 14.8613 15.0488 14.952 15.0492 15.0428C15.0498 15.1665 15.0496 15.2902 15.0493 15.4138C15.0495 15.5178 15.0495 15.5178 15.0496 15.6238C15.0357 15.7919 15.0357 15.7919 14.9222 15.9621C14.8202 15.9833 14.8202 15.9833 14.6941 15.9833C14.6461 15.9837 14.5981 15.9842 14.5487 15.9846C14.4957 15.9842 14.4427 15.9837 14.388 15.9833C14.332 15.9835 14.2759 15.9838 14.2181 15.984C14.0289 15.9845 13.8398 15.9839 13.6507 15.9832C13.5156 15.9833 13.3805 15.9836 13.2454 15.9838C12.9158 15.9843 12.5862 15.984 12.2566 15.9833C11.8731 15.9825 11.4895 15.9826 11.106 15.9827C10.4219 15.9829 9.73771 15.9823 9.05356 15.9812C8.38915 15.9801 7.72474 15.9797 7.06032 15.98C6.33745 15.9803 5.61458 15.9803 4.89171 15.9797C4.81468 15.9796 4.73766 15.9795 4.66063 15.9795C4.62272 15.9794 4.58482 15.9794 4.54577 15.9794C4.2782 15.9791 4.01063 15.9792 3.74306 15.9793C3.38317 15.9794 3.02329 15.9789 2.6634 15.978C2.53097 15.9778 2.39853 15.9777 2.2661 15.9779C2.08601 15.9781 1.90593 15.9776 1.72584 15.9769C1.67282 15.9771 1.61981 15.9773 1.56518 15.9775C1.5172 15.9772 1.46921 15.9769 1.41978 15.9765C1.37817 15.9765 1.33656 15.9764 1.2937 15.9764C1.1917 15.9621 1.1917 15.9621 1.07822 15.8486C1.02094 15.6397 1.01436 15.4408 1.01428 15.225C1.01412 15.1567 1.01396 15.0885 1.01379 15.0182C1.01391 14.9438 1.01402 14.8695 1.01414 14.7928C1.01406 14.7133 1.01396 14.6337 1.01384 14.5542C1.01358 14.3384 1.01368 14.1225 1.01386 13.9067C1.01401 13.6799 1.01387 13.4531 1.01378 13.2263C1.01368 12.8447 1.01382 12.4631 1.01409 12.0816C1.01439 11.6427 1.0143 11.2039 1.01398 10.765C1.01372 10.3868 1.01369 10.0087 1.01384 9.6305C1.01392 9.40529 1.01394 9.18008 1.01375 8.95487C1.01244 7.15744 1.01244 7.15744 1.07822 6.37342C1.01947 6.37614 0.960724 6.37886 0.900195 6.38167C0.479816 6.40113 0.479816 6.40113 0.283894 6.37342C0.139116 6.22992 0.0649042 6.13443 0.0288882 5.93222C0.0290499 5.87507 0.0292117 5.81793 0.0293783 5.75905C0.0288546 5.66135 0.0288546 5.66135 0.0283203 5.56168C0.0291357 5.49188 0.0299511 5.42207 0.0307912 5.35015C0.0308432 5.27805 0.0308951 5.20595 0.0309487 5.13166C0.0312913 4.97935 0.0320624 4.82712 0.0335892 4.67483C0.0355191 4.47991 0.035951 4.28502 0.0358649 4.09009C0.0359032 3.90372 0.0369064 3.71737 0.0378834 3.531C0.0380096 3.46108 0.0381359 3.39116 0.038266 3.31912C0.03899 3.25417 0.0397139 3.18922 0.0404598 3.1223C0.0408924 3.0652 0.0413249 3.0081 0.0417706 2.94927C0.0573661 2.79477 0.0863094 2.70167 0.170419 2.572C0.338255 2.48809 0.508275 2.50835 0.692139 2.50973C0.732277 2.50981 0.772416 2.50989 0.813771 2.50997C0.942112 2.51029 1.07045 2.511 1.19879 2.51172C1.28574 2.51201 1.37269 2.51226 1.45965 2.5125C1.67293 2.51312 1.88621 2.5141 2.0995 2.51527C2.08581 2.46339 2.08581 2.46339 2.07185 2.41046C1.95411 1.91606 2.04977 1.40246 2.28611 0.95986C3.16182 -0.440345 4.86764 -0.0425383 6.12787 0.58619ZM3.11014 1.16775C2.87652 1.52221 2.81457 1.79753 2.86435 2.22028C2.88981 2.36046 2.88981 2.36046 3.0073 2.51527C3.12616 2.54007 3.12616 2.54007 3.26497 2.53459C3.31835 2.53458 3.37173 2.53457 3.42673 2.53455C3.51335 2.53323 3.51335 2.53323 3.60171 2.53189C3.66074 2.53165 3.71977 2.53141 3.78059 2.53116C3.96965 2.5302 4.15867 2.52807 4.34772 2.52591C4.47568 2.52505 4.60363 2.52428 4.73159 2.52358C5.04573 2.5217 5.35984 2.51885 5.67396 2.51527C5.67912 2.42191 5.67912 2.42191 5.68438 2.32666C5.68917 2.24414 5.69398 2.16162 5.69879 2.0791C5.70102 2.03818 5.70325 1.99726 5.70555 1.95509C5.71833 1.73983 5.73799 1.53447 5.78744 1.32378C4.99608 0.897024 3.84847 0.408318 3.11014 1.16775ZM10.3264 1.32378C10.3352 1.36086 10.3439 1.39794 10.3528 1.43615C10.3908 1.6503 10.4033 1.86216 10.4151 2.0791C10.4175 2.12112 10.42 2.16313 10.4225 2.20642C10.4285 2.30936 10.4342 2.41231 10.4399 2.51527C10.8085 2.51814 11.1771 2.52013 11.5457 2.5215C11.6711 2.52207 11.7965 2.52285 11.9219 2.52383C12.1021 2.5252 12.2822 2.52585 12.4623 2.52635C12.5466 2.52723 12.5466 2.52723 12.6326 2.52812C12.6849 2.52813 12.7371 2.52814 12.7909 2.52815C12.8369 2.5284 12.8829 2.52865 12.9303 2.52891C13.067 2.52253 13.067 2.52253 13.2201 2.40179C13.332 2.02701 13.2779 1.6691 13.1066 1.32378C12.9475 1.06636 12.7599 0.930691 12.4825 0.81314C11.7358 0.698407 10.9605 0.936903 10.3264 1.32378ZM6.46829 1.77768C6.43201 1.89872 6.43201 1.89872 6.44701 2.02945C6.44892 2.07268 6.45082 2.1159 6.45278 2.16044C6.46778 2.30619 6.46778 2.30619 6.52503 2.51527C7.53609 2.51527 8.54716 2.51527 9.58886 2.51527C9.64305 2.3527 9.6603 2.23129 9.66687 2.06137C9.66965 2.01222 9.67243 1.96307 9.67529 1.91243C9.65793 1.76722 9.65793 1.76722 9.52658 1.68083C8.72774 1.32495 7.19388 1.19937 6.46829 1.77768ZM0.85127 3.30959C0.85127 4.07725 0.85127 4.84491 0.85127 5.63583C2.33042 5.63583 3.80957 5.63583 5.33354 5.63583C5.33354 4.86818 5.33354 4.10052 5.33354 3.30959C3.85439 3.30959 2.37524 3.30959 0.85127 3.30959ZM6.1846 3.30959C6.1846 4.07725 6.1846 4.84491 6.1846 5.63583C7.42035 5.63583 8.65609 5.63583 9.92928 5.63583C9.92928 4.86818 9.92928 4.10052 9.92928 3.30959C8.69354 3.30959 7.45779 3.30959 6.1846 3.30959ZM10.7803 3.30959C10.7803 4.07725 10.7803 4.84491 10.7803 5.63583C12.2408 5.63583 13.7012 5.63583 15.2059 5.63583C15.2059 4.86818 15.2059 4.10052 15.2059 3.30959C13.7455 3.30959 12.285 3.30959 10.7803 3.30959ZM1.87255 6.37342C1.87255 9.29427 1.87255 12.2151 1.87255 15.2245C3.22063 15.2245 4.56872 15.2245 5.95765 15.2245C5.97638 12.3036 5.9951 9.38278 6.01439 6.37342C4.64758 6.37342 3.28077 6.37342 1.87255 6.37342ZM6.80872 6.37342C6.80872 9.29427 6.80872 12.2151 6.80872 15.2245C7.61382 15.2245 8.41893 15.2245 9.24843 15.2245C9.26716 12.3036 9.28588 9.38278 9.30517 6.37342C8.48134 6.37342 7.65751 6.37342 6.80872 6.37342ZM10.0995 6.37342C10.0995 9.29427 10.0995 12.2151 10.0995 15.2245C11.4663 15.2245 12.8331 15.2245 14.2413 15.2245C14.2413 12.3036 14.2413 9.38278 14.2413 6.37342C12.8745 6.37342 11.5077 6.37342 10.0995 6.37342Z"
                          fill="#123860"
                        ></path>
                      </g>
                      <defs>
                        <clipPath id="clip0_7471_58273">
                          <rect width="16" height="16" fill="white"></rect>
                        </clipPath>
                      </defs>
                    </svg>
                    {{discount.discount}}
                  </span>
                </a>
              </div>
            </template>
            <template v-else>
              <div
                class="card_discount"
                v-for="discount in discountInfo"
                :key="discount.key"
              >
                <a :href="'/tour/'+discount.firstGroupCode" target="_blank">
                  <span class="card_discount-main">
                    <svg
                      class="icon-discount_gift"
                      xmlns="http://www.w3.org/2000/svg"
                      width="16"
                      height="16"
                      viewBox="0 0 16 16"
                      fill="none"
                    >
                      <g clip-path="url(#clip0_7471_58273)">
                        <path
                          d="M6.12787 0.58619C6.19527 0.628836 6.2625 0.671753 6.32955 0.714957C6.56604 0.833739 6.66883 0.820045 6.92574 0.770587C7.30428 0.708581 7.67337 0.685613 8.05694 0.68548C8.11031 0.685316 8.16368 0.685151 8.21866 0.684982C8.50216 0.687441 8.76744 0.70965 9.04547 0.763633C9.60636 0.870676 9.92216 0.688021 10.3927 0.415866C11.0233 0.0724135 11.8931 -0.0953636 12.5984 0.0558261C13.1122 0.20757 13.5509 0.476311 13.8444 0.934372C14.1148 1.4401 14.1308 1.95975 14.0144 2.51527C14.0665 2.51438 14.1186 2.51348 14.1722 2.51257C14.366 2.50966 14.5597 2.50753 14.7534 2.50592C14.8372 2.50506 14.9209 2.5039 15.0047 2.50243C15.1252 2.50036 15.2458 2.4994 15.3663 2.49865C15.4751 2.49731 15.4751 2.49731 15.586 2.49594C15.7922 2.51723 15.8568 2.53994 16.0002 2.68548C16.0286 2.83835 16.0286 2.83835 16.0287 3.02502C16.0295 3.12982 16.0295 3.12982 16.0303 3.23673C16.0297 3.31303 16.029 3.38933 16.0284 3.46562C16.0284 3.54384 16.0284 3.62206 16.0286 3.70027C16.0287 3.86416 16.028 4.02802 16.0267 4.19191C16.0251 4.40198 16.0252 4.61201 16.0259 4.82208C16.0263 4.98359 16.0259 5.14508 16.0252 5.30658C16.0249 5.38404 16.0249 5.46149 16.0251 5.53895C16.0253 5.64719 16.0242 5.75543 16.0231 5.86367C16.0228 5.92527 16.0225 5.98687 16.0222 6.05034C16.0002 6.20321 16.0002 6.20321 15.9392 6.29721C15.7712 6.41445 15.6236 6.38961 15.4222 6.38406C15.35 6.38253 15.2778 6.38099 15.2034 6.37941C15.1204 6.37645 15.1204 6.37645 15.0357 6.37342C15.0358 6.446 15.0358 6.446 15.036 6.52004C15.0385 7.69624 15.0404 8.87244 15.0415 10.0486C15.0421 10.6174 15.0429 11.1862 15.0442 11.755C15.0454 12.2507 15.0461 12.7463 15.0463 13.242C15.0465 13.5045 15.0468 13.7671 15.0477 14.0296C15.0484 14.2766 15.0487 14.5235 15.0485 14.7705C15.0485 14.8613 15.0488 14.952 15.0492 15.0428C15.0498 15.1665 15.0496 15.2902 15.0493 15.4138C15.0495 15.5178 15.0495 15.5178 15.0496 15.6238C15.0357 15.7919 15.0357 15.7919 14.9222 15.9621C14.8202 15.9833 14.8202 15.9833 14.6941 15.9833C14.6461 15.9837 14.5981 15.9842 14.5487 15.9846C14.4957 15.9842 14.4427 15.9837 14.388 15.9833C14.332 15.9835 14.2759 15.9838 14.2181 15.984C14.0289 15.9845 13.8398 15.9839 13.6507 15.9832C13.5156 15.9833 13.3805 15.9836 13.2454 15.9838C12.9158 15.9843 12.5862 15.984 12.2566 15.9833C11.8731 15.9825 11.4895 15.9826 11.106 15.9827C10.4219 15.9829 9.73771 15.9823 9.05356 15.9812C8.38915 15.9801 7.72474 15.9797 7.06032 15.98C6.33745 15.9803 5.61458 15.9803 4.89171 15.9797C4.81468 15.9796 4.73766 15.9795 4.66063 15.9795C4.62272 15.9794 4.58482 15.9794 4.54577 15.9794C4.2782 15.9791 4.01063 15.9792 3.74306 15.9793C3.38317 15.9794 3.02329 15.9789 2.6634 15.978C2.53097 15.9778 2.39853 15.9777 2.2661 15.9779C2.08601 15.9781 1.90593 15.9776 1.72584 15.9769C1.67282 15.9771 1.61981 15.9773 1.56518 15.9775C1.5172 15.9772 1.46921 15.9769 1.41978 15.9765C1.37817 15.9765 1.33656 15.9764 1.2937 15.9764C1.1917 15.9621 1.1917 15.9621 1.07822 15.8486C1.02094 15.6397 1.01436 15.4408 1.01428 15.225C1.01412 15.1567 1.01396 15.0885 1.01379 15.0182C1.01391 14.9438 1.01402 14.8695 1.01414 14.7928C1.01406 14.7133 1.01396 14.6337 1.01384 14.5542C1.01358 14.3384 1.01368 14.1225 1.01386 13.9067C1.01401 13.6799 1.01387 13.4531 1.01378 13.2263C1.01368 12.8447 1.01382 12.4631 1.01409 12.0816C1.01439 11.6427 1.0143 11.2039 1.01398 10.765C1.01372 10.3868 1.01369 10.0087 1.01384 9.6305C1.01392 9.40529 1.01394 9.18008 1.01375 8.95487C1.01244 7.15744 1.01244 7.15744 1.07822 6.37342C1.01947 6.37614 0.960724 6.37886 0.900195 6.38167C0.479816 6.40113 0.479816 6.40113 0.283894 6.37342C0.139116 6.22992 0.0649042 6.13443 0.0288882 5.93222C0.0290499 5.87507 0.0292117 5.81793 0.0293783 5.75905C0.0288546 5.66135 0.0288546 5.66135 0.0283203 5.56168C0.0291357 5.49188 0.0299511 5.42207 0.0307912 5.35015C0.0308432 5.27805 0.0308951 5.20595 0.0309487 5.13166C0.0312913 4.97935 0.0320624 4.82712 0.0335892 4.67483C0.0355191 4.47991 0.035951 4.28502 0.0358649 4.09009C0.0359032 3.90372 0.0369064 3.71737 0.0378834 3.531C0.0380096 3.46108 0.0381359 3.39116 0.038266 3.31912C0.03899 3.25417 0.0397139 3.18922 0.0404598 3.1223C0.0408924 3.0652 0.0413249 3.0081 0.0417706 2.94927C0.0573661 2.79477 0.0863094 2.70167 0.170419 2.572C0.338255 2.48809 0.508275 2.50835 0.692139 2.50973C0.732277 2.50981 0.772416 2.50989 0.813771 2.50997C0.942112 2.51029 1.07045 2.511 1.19879 2.51172C1.28574 2.51201 1.37269 2.51226 1.45965 2.5125C1.67293 2.51312 1.88621 2.5141 2.0995 2.51527C2.08581 2.46339 2.08581 2.46339 2.07185 2.41046C1.95411 1.91606 2.04977 1.40246 2.28611 0.95986C3.16182 -0.440345 4.86764 -0.0425383 6.12787 0.58619ZM3.11014 1.16775C2.87652 1.52221 2.81457 1.79753 2.86435 2.22028C2.88981 2.36046 2.88981 2.36046 3.0073 2.51527C3.12616 2.54007 3.12616 2.54007 3.26497 2.53459C3.31835 2.53458 3.37173 2.53457 3.42673 2.53455C3.51335 2.53323 3.51335 2.53323 3.60171 2.53189C3.66074 2.53165 3.71977 2.53141 3.78059 2.53116C3.96965 2.5302 4.15867 2.52807 4.34772 2.52591C4.47568 2.52505 4.60363 2.52428 4.73159 2.52358C5.04573 2.5217 5.35984 2.51885 5.67396 2.51527C5.67912 2.42191 5.67912 2.42191 5.68438 2.32666C5.68917 2.24414 5.69398 2.16162 5.69879 2.0791C5.70102 2.03818 5.70325 1.99726 5.70555 1.95509C5.71833 1.73983 5.73799 1.53447 5.78744 1.32378C4.99608 0.897024 3.84847 0.408318 3.11014 1.16775ZM10.3264 1.32378C10.3352 1.36086 10.3439 1.39794 10.3528 1.43615C10.3908 1.6503 10.4033 1.86216 10.4151 2.0791C10.4175 2.12112 10.42 2.16313 10.4225 2.20642C10.4285 2.30936 10.4342 2.41231 10.4399 2.51527C10.8085 2.51814 11.1771 2.52013 11.5457 2.5215C11.6711 2.52207 11.7965 2.52285 11.9219 2.52383C12.1021 2.5252 12.2822 2.52585 12.4623 2.52635C12.5466 2.52723 12.5466 2.52723 12.6326 2.52812C12.6849 2.52813 12.7371 2.52814 12.7909 2.52815C12.8369 2.5284 12.8829 2.52865 12.9303 2.52891C13.067 2.52253 13.067 2.52253 13.2201 2.40179C13.332 2.02701 13.2779 1.6691 13.1066 1.32378C12.9475 1.06636 12.7599 0.930691 12.4825 0.81314C11.7358 0.698407 10.9605 0.936903 10.3264 1.32378ZM6.46829 1.77768C6.43201 1.89872 6.43201 1.89872 6.44701 2.02945C6.44892 2.07268 6.45082 2.1159 6.45278 2.16044C6.46778 2.30619 6.46778 2.30619 6.52503 2.51527C7.53609 2.51527 8.54716 2.51527 9.58886 2.51527C9.64305 2.3527 9.6603 2.23129 9.66687 2.06137C9.66965 2.01222 9.67243 1.96307 9.67529 1.91243C9.65793 1.76722 9.65793 1.76722 9.52658 1.68083C8.72774 1.32495 7.19388 1.19937 6.46829 1.77768ZM0.85127 3.30959C0.85127 4.07725 0.85127 4.84491 0.85127 5.63583C2.33042 5.63583 3.80957 5.63583 5.33354 5.63583C5.33354 4.86818 5.33354 4.10052 5.33354 3.30959C3.85439 3.30959 2.37524 3.30959 0.85127 3.30959ZM6.1846 3.30959C6.1846 4.07725 6.1846 4.84491 6.1846 5.63583C7.42035 5.63583 8.65609 5.63583 9.92928 5.63583C9.92928 4.86818 9.92928 4.10052 9.92928 3.30959C8.69354 3.30959 7.45779 3.30959 6.1846 3.30959ZM10.7803 3.30959C10.7803 4.07725 10.7803 4.84491 10.7803 5.63583C12.2408 5.63583 13.7012 5.63583 15.2059 5.63583C15.2059 4.86818 15.2059 4.10052 15.2059 3.30959C13.7455 3.30959 12.285 3.30959 10.7803 3.30959ZM1.87255 6.37342C1.87255 9.29427 1.87255 12.2151 1.87255 15.2245C3.22063 15.2245 4.56872 15.2245 5.95765 15.2245C5.97638 12.3036 5.9951 9.38278 6.01439 6.37342C4.64758 6.37342 3.28077 6.37342 1.87255 6.37342ZM6.80872 6.37342C6.80872 9.29427 6.80872 12.2151 6.80872 15.2245C7.61382 15.2245 8.41893 15.2245 9.24843 15.2245C9.26716 12.3036 9.28588 9.38278 9.30517 6.37342C8.48134 6.37342 7.65751 6.37342 6.80872 6.37342ZM10.0995 6.37342C10.0995 9.29427 10.0995 12.2151 10.0995 15.2245C11.4663 15.2245 12.8331 15.2245 14.2413 15.2245C14.2413 12.3036 14.2413 9.38278 14.2413 6.37342C12.8745 6.37342 11.5077 6.37342 10.0995 6.37342Z"
                          fill="#123860"
                        ></path>
                      </g>
                      <defs>
                        <clipPath id="clip0_7471_58273">
                          <rect width="16" height="16" fill="white"></rect>
                        </clipPath>
                      </defs>
                    </svg>
                    {{discount.discount}}
                  </span>

                  <span class="card_discount-note">
                    *優惠方案之適用日期：{{discount.leaveDateStr}}
                  </span>
                </a>
              </div>
            </template>

            <div class="card_keywords" v-if="item.tag">
              <svg
                class="icon-discount_keywords"
                xmlns="http://www.w3.org/2000/svg"
                width="16"
                height="18"
                viewBox="0 0 16 18"
                fill="none"
              >
                <g clip-path="url(#clip0_1375_17194)">
                  <path
                    d="M6.25028 13.1667H10.7497M7.00018 15.5H9.99977M6.25028 10.8333C6.25103 9.27778 5.87609 8.88889 5.12544 8.11111C4.37479 7.33333 4.01934 6.54544 4.00059 5.38889C3.96535 3.01667 5.50039 1.5 8.49997 1.5C11.5003 1.5 13.0361 3.01667 12.9994 5.38889C12.9821 6.54544 12.6244 7.33333 11.8745 8.11111C11.1254 8.88889 10.7504 9.27778 10.7497 10.8333"
                    stroke="#C4640D"
                    stroke-width="1.2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  ></path>
                </g>
                <defs>
                  <clipPath id="clip0_1375_17194">
                    <rect
                      width="16"
                      height="16.99"
                      fill="white"
                      transform="translate(0 0.503906)"
                    ></rect>
                  </clipPath>
                </defs>
              </svg>

              {{item.tag}}
            </div>
            <div
              class="card_feature"
              v-if="earliestGroup.groupMemo && groupShowType === 'uni'"
            >
              <svg
                class="icon-feature"
                xmlns="http://www.w3.org/2000/svg"
                width="16"
                height="17"
                viewBox="0 0 16 17"
                fill="none"
              >
                <path
                  d="M4.25 5.5H11.75M4.25 8.61111H11.75M4.25 11.7222H9.5M3 15.5H13C13.5523 15.5 14 15.0523 14 14.5V2.5C14 1.94772 13.5523 1.5 13 1.5H3C2.44772 1.5 2 1.94772 2 2.5V14.5C2 15.0523 2.44772 15.5 3 15.5Z"
                  stroke="#A99F96"
                ></path>
              </svg>
              {{earliestGroup.groupMemo}}
            </div>
          </div>
        </div>
        <div class="D">
          <ul class="product_label" v-if="item.hashTagList.length">
            <li v-for="hashTag in item.hashTagList" :key="hashTag">
              {{hashTag}}
            </li>
          </ul>
        </div>
        <div class="E">
          <div
            class="cruiseroom_sheet"
            @click="getCruises"
            v-if="item.hasCruisePrice"
          >
            <span>艙房售價表</span>
            <i class="fa-solid fa-chevron-right fa-xs"></i>
          </div>
          <div class="depart_location">{{item.departure}}出發</div>
        </div>
        <div class="G">
          <div
            class="cruiseroom_sheet"
            @click="getCruises"
            v-if="item.hasCruisePrice"
          >
            <span>艙房售價表</span>
            <i class="fa-solid fa-chevron-right fa-xs"></i>
          </div>
          <div class="card_date_wrap">
            <ul>
              <li
                v-for="groupDate in visibleGroupDates"
                :key="groupDate.groupCode"
                :class="groupDate.type"
                @click="goProduct(groupDate.groupCode,$event)"
              >
                {{groupDate.leaveDate}}
              </li>
            </ul>
            <div class="card_date_more" v-if="showMoreIcon && showMoreText">
              <svg
                class="icon-more_date"
                xmlns="http://www.w3.org/2000/svg"
                width="19"
                height="19"
                viewBox="0 0 19 19"
                fill="none"
              >
                <circle cx="9.5" cy="9.5" r="8.5" stroke="#736357"></circle>
                <circle
                  cx="5.19652"
                  cy="9.26"
                  r="1.21312"
                  fill="#736357"
                ></circle>
                <circle
                  cx="9.30296"
                  cy="9.26"
                  r="1.21312"
                  fill="#736357"
                ></circle>
                <circle
                  cx="13.2219"
                  cy="9.26"
                  r="1.21312"
                  fill="#736357"
                ></circle>
              </svg>

              <div class="card_date_more_txt">...</div>
            </div>
          </div>
          <div class="flight_info_wrap">
            <div class="flight_position">
              {{groupShowType === 'uni' ? group.totalSeat : 0}}
            </div>
            <div class="flight_amount">
              {{ groupShowType === 'uni' ? (group.waitSeat >= group.remainSeat
              || group.remainSeat <= 0 ? 0 : group.remainSeat) : 0 }}
            </div>
            <div
              v-if="item.hasFlight"
              class="flight_check"
              tabindex="0"
              @click="getFlights"
            >
              航班
              <i class="fa-solid fa-circle-chevron-down"></i>
            </div>
          </div>
        </div>
        <div class="H">
          <div class="product_price_wrap">
            <template v-if="groupMinPrice.price===99999999">
              <div>載入中...</div>
            </template>
            <template v-else>
              <div
                :id="((isAllProduct || groupShowType === 'uni') ? earliestGroup.groupCode : item.mainGroupCode)"
                class="favro fav-btn compare_0"
                name="compare"
                :grup_cd="((isAllProduct || groupShowType === 'uni') ? earliestGroup.groupCode : item.mainGroupCode)"
                @click="favorite($event)"
              >
                <svg
                  class="icon-favro"
                  xmlns="http://www.w3.org/2000/svg"
                  width="20"
                  height="18"
                  viewBox="0 0 20 18"
                  fill="none"
                >
                  <path
                    d="M3.52051 1.25488C4.85621 0.699639 6.1397 0.862288 7.35449 1.69141V1.69238C7.84078 2.02699 8.25402 2.4377 8.60645 2.91016L8.75391 3.11719H8.75488C8.78234 3.15714 8.81414 3.19916 8.83008 3.2207L8.86426 3.26953C9.11064 3.70513 9.5161 3.99217 9.99414 3.99121C10.4704 3.99006 10.8783 3.7028 11.1318 3.28223L11.1328 3.2832C11.2653 3.06686 11.4238 2.85592 11.5996 2.65039L11.7812 2.44727V2.44629C12.3977 1.77302 13.0983 1.28186 13.9434 1.07715L14.1143 1.04004H14.1172C15.5253 0.762647 16.7365 1.12439 17.7822 2.03223L17.9883 2.2207H17.9893C18.9529 3.14203 19.4865 4.42292 19.498 5.73633L19.4951 5.74316L19.4941 5.8623C19.4803 7.49979 18.9238 8.97227 18.0078 10.3613L17.8203 10.6377C16.8933 11.965 15.7399 13.0947 14.4521 14.1191L13.8926 14.5518C12.664 15.4748 11.357 16.285 9.99805 17.0361C8.38709 16.1403 6.84614 15.1686 5.4375 14.0312L4.8291 13.5225C3.50646 12.37 2.35878 11.1093 1.54102 9.61816L1.38184 9.31641C0.793702 8.14759 0.448014 6.96669 0.505859 5.73633L0.523438 5.49023V5.4873C0.69258 3.5092 1.62758 2.04289 3.51953 1.25391L3.52051 1.25488Z"
                    stroke="#736357"
                  ></path>
                </svg>

                <span>
                  加入
                  <br />
                  最愛
                </span>
              </div>
              <div class="price_2">
                {{groupMinPrice.price.toLocaleString()}}
              </div>
              <div class="price">
                {{groupMinPrice.sitePrice.toLocaleString()}}
              </div>
            </template>
          </div>
        </div>
      </div>
      <div class="flight_wrap">
        <div class="flight_toggle" @click="toggleFlightDetails">收起</div>

        <!-- 航班結構 -->
        <div class="row">
          <div
            class="card_group"
            v-for="(flight,idx) in flights"
            :key="flight[0]?.bookSq || flight[0]?.flightNo || idx"
          >
            <div class="card_group_title">航班{{idx+1}}</div>
            <flight-info
              v-for="oFlight in flight"
              :key="oFlight.bookSq || ((oFlight.flightNo || 'flight') + '-' + (oFlight.depDate || oFlight.depTime || '') + '-' + (oFlight.arrDate || oFlight.arrTime || ''))"
              :flight="oFlight"
            />
          </div>
        </div>
        <div class="flight_note">
          以上為參考航班．
          <br />
          實際航班時間及轉機點以說明會資料為準
        </div>
        <!-- 航班結構結束 -->
      </div>
    </a>
  `,
  methods: {
    getStatusClass(status) {
      return `type${status}`;
    },
    removeParenthesesContent(str) {
      return str.replace(/（.*?）/g, "");
    },
  },
});
